import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  model,
  output,
  viewChild,
  inject,
} from '@angular/core';
import { MatFormField, MatFormFieldModule } from '@angular/material/form-field';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerInputEvent,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatInput, MatInputModule } from '@angular/material/input';
import {
  DateAdapter,
  MatNativeDateModule,
  provideNativeDateAdapter,
} from '@angular/material/core';
import { NgClass } from '@angular/common';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { DATEPICKER_FORMAT_M, MAX_DATE_M, MIN_DATE_M } from '@core-utils';
import { SwitchLanguageService } from '../../services';
import {
  AbstractControl,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ValidationFormComponent } from '../validation-form/validation-form.component';

@Component({
  selector: 'ui-form-datepicker',
  imports: [
    MatFormField,
    MatDatepicker,
    MatInput,
    MatDatepickerInput,
    MatFormFieldModule,
    MatNativeDateModule,
    NgClass,
    MatDatepickerModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    ValidationFormComponent,
  ],
  templateUrl: './form-datepicker.component.html',
  styleUrl: './form-datepicker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: DATEPICKER_FORMAT_M },
    provideNativeDateAdapter(),
  ],
  host: { '[attr.data-cy]': 'dataCy() || null' },
})
export class FormDatepickerComponent {
  dataCy = input<string>('');
  private dateAdapter = inject<DateAdapter<Date>>(DateAdapter);
  private switchLanguageService = inject(SwitchLanguageService);

  control = input<AbstractControl>();
  minDate = input<Date>(MIN_DATE_M);
  maxDate = input<Date>(MAX_DATE_M);
  placeholder = input<string>('Choose Date');
  global = input<boolean>(false);
  withoutTime = input<boolean>(false);
  description = input<string>();
  panelClass = input<string>('');
  startView = input<'month' | 'year' | 'multi-year'>('month');
  startAt = input<Date | null>(null);
  selectedDate = model<string | Date | null>(new Date());
  pickerMat = viewChild.required<MatDatepicker<Date>>('pickerMat');

  dateChange = output<string | Date | null>();

  constructor() {
    effect(() => {
      this.switchLanguageService.currentLang();
      this.dateAdapter.setLocale(this.switchLanguageService.currentLang());
    });
  }

  get formControl(): FormControl {
    return this.control() as FormControl;
  }

  onDateChange(event: MatDatepickerInputEvent<Date>) {
    if (event?.value) {
      const utcDateString = this.toUTCDateString(event.value);
      this.dateChange.emit(utcDateString);

      const control = this.control();
      if (control) {
        control.setValue(utcDateString);
      }
    }
  }

  onButtonClick() {
    if (this.selectedDate()) {
      this.selectedDate.set(null);
      this.dateChange.emit(null);
    } else {
      this.pickerMat().open();
    }
  }
  onButtonClickWithControl() {
    if (this.formControl.value) {
      this.formControl.setValue(null);
    } else {
      this.pickerMat().open();
    }
  }

  private toUTCDateString(date: Date): string {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    const utcDate = new Date(Date.UTC(year, month, day, 0, 0, 0));

    if (isNaN(utcDate.getTime())) {
      return date.toISOString();
    }

    if (this.withoutTime()) {
      return utcDate.toISOString().split('T')[0];
    } else {
      return utcDate.toISOString();
    }
  }
}
